import { FC } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { usePlatformRecovery } from "@/hooks/use-platform-unavailable";
import type { PlatformUnavailableReason } from "@/lib/platform-unavailable";

// Inline copy of public/logo_square.svg: while the platform is unavailable every
// non-API path is answered with an HTML page, so an <img src> only renders if the
// service worker happens to have it precached.
const LogoMark: FC = () => (
    <svg
        aria-label="Logo"
        className="h-16 w-16"
        role="img"
        viewBox="0 0 906 911"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            d="M 107.759 63.239 L 0.018 125.500 0.050 251 L 0.083 376.500 107.661 438.500 C 166.829 472.600, 216.060 500.500, 217.064 500.500 C 218.067 500.500, 267.274 472.600, 326.414 438.500 L 433.940 376.500 433.970 250.986 L 434 125.472 326.750 63.552 C 267.762 29.497, 218.600 1.486, 217.500 1.305 C 216.265 1.103, 174.268 24.805, 107.759 63.239 M 344.246 471.768 L 235.992 534.376 236.246 659.844 L 236.500 785.312 344.470 847.656 C 403.853 881.945, 452.734 910, 453.095 910 C 453.456 910, 502.294 881.970, 561.625 847.712 L 669.500 785.424 669.754 659.849 L 670.008 534.274 561.519 471.637 C 501.850 437.187, 452.910 409.036, 452.765 409.080 C 452.619 409.124, 403.786 437.333, 344.246 471.768"
            fill="#343c4c"
            fillRule="evenodd"
        />
        <path
            d="M 580.500 62.415 L 472.500 124.796 472.246 250.752 L 471.992 376.708 580.554 439.391 L 689.115 502.073 797.308 439.599 L 905.500 377.125 905.500 250.883 L 905.500 124.642 797.538 62.321 C 738.160 28.044, 689.335 0.008, 689.038 0.017 C 688.742 0.026, 639.900 28.105, 580.500 62.415"
            fill="#44b364"
            fillRule="evenodd"
        />
    </svg>
);

const PlatformUnavailable: FC<{ reason: PlatformUnavailableReason }> = ({
    reason,
}) => {
    const { t } = useTranslation();
    usePlatformRecovery();

    return (
        <div className="flex min-h-svh w-full items-center justify-center p-6 md:p-10">
            <div className="w-full max-w-md flex flex-col items-center gap-6 text-center">
                <LogoMark />
                <h1 className="text-2xl font-semibold">
                    {t(`pages.platform-unavailable.${reason}.title`)}
                </h1>
                <p className="text-muted-foreground">
                    {t(`pages.platform-unavailable.${reason}.description`)}
                </p>
                <p className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t("pages.platform-unavailable.auto-reload")}
                </p>
            </div>
        </div>
    );
};

export default PlatformUnavailable;
